import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../lib/api';

/**
 * Shared wizard helper for the "Property details" step:
 * - Checks whether an email already has an account (debounced).
 * - When the user clicks Continue, if they typed a password and the account
 *   does NOT exist, registers them; if the sister-app (EasySpanishTax) owns
 *   the email, tries to log them in so both apps stay linked.
 *
 * Returns { emailStatus, authError, advanceFromOwnerStep } plus helpers for
 * the password field UI (`showPassword`, `passwordDisabled`).
 */
export function useWizardAutoAuth({ email, password, name, t, UI }) {
  const T = (k) => t(k, UI[k]);
  const { user, login: authLogin, register: authRegister } = useAuth();

  const [emailStatus, setEmailStatus] = useState({ checking: false, exists: null, email: '' });
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    const e = (email || '').trim().toLowerCase();
    if (!e || !e.includes('@')) {
      setEmailStatus({ checking: false, exists: null, email: e });
      return;
    }
    if (user) {
      setEmailStatus({ checking: false, exists: true, email: e });
      return;
    }
    setEmailStatus((s) => ({ ...s, checking: true, email: e }));
    const tm = setTimeout(() => {
      authApi.checkEmail(e)
        .then((r) => setEmailStatus({ checking: false, exists: !!r.exists, email: e }))
        .catch(() => setEmailStatus({ checking: false, exists: null, email: e }));
    }, 350);
    return () => clearTimeout(tm);
  }, [email, user]);

  const advanceFromOwnerStep = async () => {
    if (user) return true;
    const e = (email || '').trim().toLowerCase();
    if (!e || !e.includes('@')) return true;
    if (emailStatus.exists === true) return true;
    const pw = (password || '').trim();
    if (!pw) { setAuthError(T('wf.password_required')); return false; }
    if (pw.length < 6) { setAuthError(T('wf.password_min')); return false; }
    setAuthError('');
    try {
      await authRegister(name || e.split('@')[0], e, pw);
      setEmailStatus({ checking: false, exists: true, email: e });
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'string' && /already registered on EasySpanishTax/i.test(detail)) {
        try {
          await authLogin(e, pw);
          setEmailStatus({ checking: false, exists: true, email: e });
          return true;
        } catch (_e) {
          setAuthError(T('wf.password_wrong_sso'));
          return false;
        }
      }
      setAuthError(typeof detail === 'string' ? detail : (err.message || 'Signup failed'));
      return false;
    }
  };

  const passwordDisabled = !!user || emailStatus.exists === true;
  const passwordPlaceholder = user
    ? T('wf.password_logged_in')
    : emailStatus.exists === true
      ? T('wf.password_existing_ph')
      : T('wf.password_ph');

  return {
    isLoggedIn: !!user,
    emailStatus,
    authError,
    advanceFromOwnerStep,
    passwordDisabled,
    passwordPlaceholder,
  };
}
